import { API_V1 } from './APIs/APIv1';
import config from '../config';

export type FeedbackStatus = 'open' | 'in_progress' | 'done';
export type FeedbackPriority = 'low' | 'medium' | 'high';

export type FeedbackAuthorRole = 'reporter' | 'team';

export interface FeedbackMessage {
  _id: string;
  role: FeedbackAuthorRole;
  authorName?: string;
  authorUserId?: string;
  body: string;
  createdAt: string;
}

export interface FeedbackTicket {
  _id: string;
  tenantId: string;
  userId?: string;
  userEmail: string;
  title: string;
  description: string;
  screenshotFileId?: string;
  pageUrl?: string;
  userAgent?: string;
  status: FeedbackStatus;
  priority: FeedbackPriority;
  tags: string[];
  adminNote?: string;
  /** The conversation on the ticket, oldest first. */
  messages?: FeedbackMessage[];
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackListResult {
  items: FeedbackTicket[];
  total: number;
}

export interface ListFeedbackParams {
  status?: FeedbackStatus;
  priority?: FeedbackPriority;
  tenantId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface UpdateFeedbackPayload {
  status?: FeedbackStatus;
  priority?: FeedbackPriority;
  adminNote?: string;
  tags?: string[];
}

export interface SubmitFeedbackPayload {
  title: string;
  description: string;
  pageUrl?: string;
  screenshotBase64?: string;
}

export const submitFeedback = async (payload: SubmitFeedbackPayload): Promise<FeedbackTicket> => {
  const response = await API_V1.post<FeedbackTicket>('/feedback', payload);
  return response.data;
};

export const getFeedbackTickets = async (params: ListFeedbackParams = {}): Promise<FeedbackListResult> => {
  const response = await API_V1.get<FeedbackListResult>('/feedback', { params });
  return response.data;
};

export const getFeedbackTicket = async (id: string): Promise<FeedbackTicket> => {
  const response = await API_V1.get<FeedbackTicket>(`/feedback/${id}`);
  return response.data;
};

export const updateFeedbackTicket = async (id: string, payload: UpdateFeedbackPayload): Promise<FeedbackTicket> => {
  const response = await API_V1.patch<FeedbackTicket>(`/feedback/${id}`, payload);
  return response.data;
};

/** Tickets the signed-in user reported themselves, with their conversations. */
export const getMyFeedbackTickets = async (): Promise<FeedbackListResult> => {
  const response = await API_V1.get('/feedback/mine');
  return response.data;
};

/**
 * Add a reply to a ticket. The backend decides whether it lands as `team` or `reporter`
 * from the caller's role, and refuses a reporter writing on someone else's ticket.
 */
export const replyToFeedbackTicket = async (id: string, body: string): Promise<FeedbackTicket> => {
  const response = await API_V1.post(`/feedback/${id}/messages`, { body });
  return response.data;
};

export const getFeedbackScreenshotUrl = (id: string): string => {
  const baseUrl = (config as any).apiBaseUrl || '/api/v1';
  return `${baseUrl}/feedback/${id}/screenshot`;
};
